import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const styles = `
  html, body, #preview-root { margin: 0; width: 100%; height: 100%; background: #020617; color: #e2e8f0; font: 14px system-ui, sans-serif; }
  * { box-sizing: border-box; }
  main { height: 100%; display: flex; flex-direction: column; }
  header { display: flex; align-items: center; gap: 12px; padding: 12px 16px; flex-wrap: wrap; }
  strong { margin-right: auto; }
  button { background: #1e293b; color: #f8fafc; border: 1px solid #64748b; border-radius: 6px; padding: 8px 12px; cursor: pointer; }
  button:disabled { opacity: .5; cursor: default; }
  .picture { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; }
  img { width: 100%; height: 100%; object-fit: contain; }
  footer { padding: 8px 16px; color: #94a3b8; }
`;

// A portal shares the dashboard's capture state without starting another polling loop.
export default function PreviewPopout({ name, image, caption, onRefresh, refreshDisabled, onOpen, onVisibilityChange }) {
  const popupRef = useRef(null);
  const [target, setTarget] = useState(null);
  const [message, setMessage] = useState('');
  const visibilityCallback = useRef(onVisibilityChange);
  visibilityCallback.current = onVisibilityChange;

  useEffect(() => {
    if (!target) return undefined;
    const popup = popupRef.current;
    const doc = target.ownerDocument;
    const updateVisibility = () => visibilityCallback.current?.(!popup.closed && !doc.hidden);
    const closed = () => {
      popupRef.current = null;
      setTarget(null);
      visibilityCallback.current?.(false);
    };
    popup.addEventListener('pagehide', closed);
    doc.addEventListener('visibilitychange', updateVisibility);
    // Some browsers do not deliver pagehide when a separate window is closed.
    const timer = window.setInterval(() => { if (popup.closed) closed(); }, 500);
    updateVisibility();
    return () => {
      window.clearInterval(timer);
      popup.removeEventListener('pagehide', closed);
      doc.removeEventListener('visibilitychange', updateVisibility);
      popup.close();
      popupRef.current = null;
      visibilityCallback.current?.(false);
    };
  }, [target]);

  const open = () => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }
    const popup = window.open('', '', 'popup=yes,width=1280,height=800,resizable=yes,scrollbars=yes');
    if (!popup) {
      setMessage('Allow pop-ups for this site to open the preview window.');
      return;
    }
    popup.document.title = `${name} — Suitestream Preview`;
    const style = popup.document.createElement('style');
    style.textContent = styles;
    popup.document.head.appendChild(style);
    const root = popup.document.createElement('div');
    root.id = 'preview-root';
    popup.document.body.appendChild(root);
    popupRef.current = popup;
    setMessage('');
    setTarget(root);
    onOpen?.();
  };

  const fullscreen = async () => {
    const doc = popupRef.current?.document;
    try {
      if (doc.fullscreenElement) await doc.exitFullscreen();
      else if (doc.documentElement.requestFullscreen) await doc.documentElement.requestFullscreen();
      else setMessage('Use the browser fullscreen command (F11) in the preview window.');
    } catch {
      setMessage('Use the browser fullscreen command (F11) in the preview window.');
    }
  };

  return <>
    <button type="button" className="secondary" onClick={open}>{target ? 'Focus preview' : 'Pop out'}</button>
    {message && <span role="status">{message}</span>}
    {target && createPortal(<main>
      <header><strong>{name} · Preview</strong>
        {onRefresh && <button type="button" onClick={onRefresh} disabled={refreshDisabled}>Refresh Now</button>}
        <button type="button" onClick={fullscreen}>Fullscreen</button>
        <button type="button" onClick={() => setTarget(null)}>Close preview</button>
      </header>
      <div className="picture">{image ? <img src={image} alt={`Preview for ${name}`} /> : <span>Waiting for a picture…</span>}</div>
      <footer role="status">{message || caption}</footer>
    </main>, target)}
  </>;
}
